import { appendZToDateString, createBaseQuery } from '@energy-stacks/shared';
import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { environment } from '@energy-stacks/feature-config';
import { ChargingStationModel } from './chargingStationModel';
import { chargingStationDetailsNormalizer } from './normalizers/chargingStationDetailsNormalizer';
import { ChargingStationDto, EvseDto } from './chargingStationDto';
import { AddChargingStationFormData } from './addChargingStationFormData';
import { mapAddChargingStationFormDataToPayload } from './mapAddChargingStationFormDataToPayload';
import {
  ChargingStationDetailsModel,
  GeoLocation,
} from './chargingStationDetailsModel';
import { chargingStationsNormalizer } from './normalizers/chargingStationsNormalizer';
import { EvseModel } from './evseModel';
import { ConnectorModel } from './connectorModel';
import { OcppNetworkLogsModel } from './network-logs/ocppNetworkLogsModel';
import { OcppNetworkLogsBody } from './network-logs/ocppNetworkLogsBody';
import { PageNetworkLogsEntry } from './network-logs/ocppNetworkLogsDto';
import {
  eventTypeNormalizer,
  eventTypeToEnum,
} from './normalizers/eventTypeNormalizer';
import { communicationDirectionNormalizer } from './normalizers/communicationDirectionNormalizer';

type SortColumnTimestamp = 'timestamp';

// NOTE: Network logs can be sorted only by timestamp BE field
const SORT_COLUMN_TIMESTAMP: SortColumnTimestamp = 'timestamp';

export interface CsmsEditChargingStationForm {
  chargingStationName: string;
  locationId?: string;
  chargingStationGroupUuid?: string;
}

export type UpdatePricingData = {
  chargingStationIdentityKey: ChargingStationDetailsModel['identityKey'];
  evseId: EvseModel['evseId'];
  connectorId: ConnectorModel['id'];
  tariffsToUnassign: string[];
  tariffsToAssign: string[];
};

export interface ChangeCoordinatesRequestBody {
  csmsUuid: string;
  coordinates: GeoLocation;
}

export interface ChangeCoordinatesConfirmation {
  status: 'Accepted' | 'Rejected';
}

export const chargingStationsApi = createApi({
  reducerPath: 'csmsChargingStations',
  tagTypes: [
    'ChargingStations',
    'ChargingStationDetails',
    'Tariffs',
    'OcppNetworkLogs',
  ],
  baseQuery: createBaseQuery(`${environment.ocppServiceUrl}`),
  endpoints: (builder) => ({
    getChargingStations: builder.query<ChargingStationModel[], void>({
      query: () => '/chargingstations',
      providesTags: ['ChargingStations'],
      transformResponse: chargingStationsNormalizer,
    }),
    getChargingStationDetails: builder.query<
      ChargingStationDetailsModel,
      string
    >({
      query: (identityKey) => `/chargingstations/${identityKey}`,
      providesTags: (result, _error, _arg) => [
        {
          type: 'ChargingStationDetails',
          id: result?.identityKey,
        },
      ],
      transformResponse: chargingStationDetailsNormalizer,
    }),
    editChargingStation: builder.mutation<
      ChargingStationDto,
      CsmsEditChargingStationForm &
        Pick<ChargingStationDto, 'identityKey' | 'csmsUuid' | 'coordinates'>
    >({
      query: (body) => ({
        url: `/chargingstations/${body.identityKey}`,
        method: 'PUT',
        body: {
          csmsUuid: body.csmsUuid,
          chargingStationName: body.chargingStationName,
          locationId: body.locationId ? body.locationId : undefined,
          chargingStationGroupUuid: body.chargingStationGroupUuid
            ? body.chargingStationGroupUuid
            : undefined,
          coordinates:
            body.coordinates.latitude && body.coordinates.longitude
              ? {
                  latitude: body.coordinates.latitude,
                  longitude: body.coordinates.longitude,
                }
              : null,
        },
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
              { type: 'ChargingStationDetails', id: arg.identityKey },
              'ChargingStations',
            ]
          : [],
    }),

    editCoordinates: builder.mutation<
      ChangeCoordinatesConfirmation,
      {
        identityKey: string;
        csmsUuid: string;
        coordinates: GeoLocation;
      }
    >({
      query: (args) => {
        const body: ChangeCoordinatesRequestBody = {
          csmsUuid: args.csmsUuid,
          coordinates: args.coordinates,
        };

        return {
          url: `chargingstations/${args.identityKey}`,
          method: 'PUT',
          body,
        };
      },
    }),

    addChargingStation: builder.mutation<
      ChargingStationDetailsModel,
      AddChargingStationFormData
    >({
      query: (body) => ({
        url: '/chargingstations',
        method: 'POST',
        body: mapAddChargingStationFormDataToPayload(body),
      }),
      invalidatesTags: ['ChargingStations'],
      transformResponse: (chargingStation: ChargingStationDto) =>
        chargingStationDetailsNormalizer(chargingStation) ?? {
          id: '',
          csmsUuid: '',
          evses: [],
          firmware: '',
          geolocation: { latitude: '', longitude: '' },
          model: '',
          name: '',
          online: false,
          serialNumber: '',
          identityKey: '',
          status: 'PROVISIONED',
          location: {
            name: chargingStation.locationDetails.name,
            id: chargingStation.locationDetails.id,
          },
          username: chargingStation.userName,
          password: chargingStation.password,
        },
    }),
    deleteChargingStation: builder.mutation<ChargingStationDto, string>({
      query: (identityKey) => ({
        url: `/chargingstations/${identityKey}`,
        method: 'DELETE',
      }),
      async onQueryStarted(identityKey, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            chargingStationsApi.util.updateQueryData(
              'getChargingStations',
              undefined,
              (draft) => {
                const updatedList = draft.filter(
                  (chargingStation) =>
                    chargingStation.identityKey !== identityKey
                );
                return updatedList;
              }
            )
          );
        } catch {}
      },
    }),

    updatePricing: builder.mutation<EvseDto | undefined, UpdatePricingData>({
      async queryFn(arg, _queryApi, _extraOptions, updateWithBQ) {
        if (arg.tariffsToUnassign.length > 0) {
          const unassignResult = await updateWithBQ({
            url: `/evses/${arg.evseId}/connector/${arg.connectorId}/unassign`,
            method: 'PUT',
            params: {
              ids: arg.tariffsToUnassign,
            },
          });
          if (unassignResult.error)
            return { error: unassignResult.error as FetchBaseQueryError };
          if (arg.tariffsToAssign.length === 0)
            return { data: unassignResult.data as EvseDto };
        }
        if (arg.tariffsToAssign.length > 0) {
          const assignResult = await updateWithBQ({
            url: `/evses/${arg.evseId}/connector/${arg.connectorId}/assign`,
            method: 'PUT',
            params: {
              ids: arg.tariffsToAssign,
            },
          });
          if (assignResult.error)
            return { error: assignResult.error as FetchBaseQueryError };
          return {
            data: assignResult.data as EvseDto,
          };
        }
        return { data: undefined };
      },
      // Invalidates tags unconditionally because assigning might throw error after successful unassiging
      invalidatesTags: (_result, _error, arg) => [
        {
          type: 'ChargingStationDetails',
          id: arg.chargingStationIdentityKey,
        },
        'ChargingStations',
      ],
    }),

    getOcppNetworkLogs: builder.query<
      OcppNetworkLogsModel,
      OcppNetworkLogsBody
    >({
      query: (searchParams) => ({
        url: `/network-logs/${searchParams.identityKey}/search`,
        method: 'GET',
        params: {
          ...searchParams,
          eventType: searchParams.eventType
            ? eventTypeToEnum[searchParams.eventType]
            : undefined,
          sort: [`${SORT_COLUMN_TIMESTAMP},${searchParams.sort.order}`],
        },
      }),
      providesTags: ['OcppNetworkLogs'],
      transformResponse: (
        ocppNetworkLogs: PageNetworkLogsEntry
      ): OcppNetworkLogsModel => {
        return {
          totalElements: ocppNetworkLogs.totalElements ?? 0,
          totalPages: ocppNetworkLogs.totalPages ?? -1,
          content:
            ocppNetworkLogs?.content?.map((log) => ({
              communicationDirection: communicationDirectionNormalizer(
                log.webSocketInteraction
              ),
              errorStatus: log.status,
              session: log.session,
              eventType: eventTypeNormalizer(log.eventType),
              timestamp: appendZToDateString(log.timestamp),
              errorReason: log.errorReason,
            })) ?? [],
        };
      },
    }),
  }),
});

export const {
  useGetChargingStationsQuery,
  useGetChargingStationDetailsQuery,
  useEditChargingStationMutation,
  useEditCoordinatesMutation,
  useAddChargingStationMutation,
  useDeleteChargingStationMutation,
  useUpdatePricingMutation,
  useGetOcppNetworkLogsQuery,
} = chargingStationsApi;
